<template>
  <v-card flat class="supplier-statement grey lighten-5">
    <!-- ───────── Barra superior ───────── -->
    <v-toolbar flat color="primary" dark dense class="flex-grow-0">
      <v-icon left>mdi-file-account-outline</v-icon>
      <v-toolbar-title class="text-subtitle-1 font-weight-bold">
        Estado de cuenta de suplidor
        <span v-if="data" class="font-weight-regular text-body-2 ml-2 white--text text--lighten-3">· {{ data.supplier.name }}</span>
      </v-toolbar-title>
      <v-spacer></v-spacer>

      <!-- Exportar -->
      <v-menu offset-y :close-on-content-click="true">
        <template v-slot:activator="{ on, attrs }">
          <v-btn text small v-bind="attrs" v-on="on" :loading="exportingPdf || exportingExcel" :disabled="loading || !data">
            <v-icon left small>mdi-export-variant</v-icon>Exportar<v-icon right small>mdi-menu-down</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-subheader>PDF</v-subheader>
          <v-list-item @click="exportPdf('full')">
            <v-list-item-icon><v-icon small>mdi-file-pdf-box</v-icon></v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Estado de cuenta completo</v-list-item-title>
              <v-list-item-subtitle>Resumen, movimientos del período, facturas con saldo y pagos</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="exportPdf('pending')">
            <v-list-item-icon><v-icon small>mdi-file-document-alert-outline</v-icon></v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Solo facturas pendientes</v-list-item-title>
              <v-list-item-subtitle>Resumen y facturas con saldo. Ideal para enviar al suplidor</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="customDialog = true">
            <v-list-item-icon><v-icon small>mdi-tune-variant</v-icon></v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Personalizado…</v-list-item-title>
              <v-list-item-subtitle>Elige qué secciones incluir</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider></v-divider>
          <v-subheader>Excel</v-subheader>
          <v-list-item @click="exportExcel">
            <v-list-item-icon><v-icon small>mdi-microsoft-excel</v-icon></v-list-item-icon>
            <v-list-item-content><v-list-item-title>Libro con resumen, movimientos, facturas y pagos</v-list-item-title></v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn icon @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
    </v-toolbar>

    <!-- ───────── Filtros del estado de cuenta ───────── -->
    <v-sheet class="px-4 py-3 supplier-statement__filters flex-grow-0" color="white">
      <v-row dense align="center">
        <v-col cols="12" sm="auto">
          <v-menu v-model="menuStart" :close-on-content-click="false" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field :value="fmtDate(filters.dateStart) || 'Desde el inicio'" label="Fecha inicio" readonly outlined dense hide-details
                v-bind="attrs" v-on="on" prepend-inner-icon="mdi-calendar-start" style="max-width: 190px" clearable
                @click:clear="filters.dateStart = null"></v-text-field>
            </template>
            <v-date-picker v-model="filters.dateStart" locale="es" color="primary" no-title :max="filters.dateEnd"
              @input="menuStart = false"></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-menu v-model="menuEnd" :close-on-content-click="false" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field :value="fmtDate(filters.dateEnd)" label="Fecha de corte" readonly outlined dense hide-details
                v-bind="attrs" v-on="on" prepend-inner-icon="mdi-calendar-end" style="max-width: 170px"></v-text-field>
            </template>
            <v-date-picker v-model="filters.dateEnd" locale="es" color="primary" no-title :min="filters.dateStart"
              @input="menuEnd = false"></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-chip-group v-model="preset" active-class="primary white--text" mandatory column>
            <v-chip v-for="p in presets" :key="p.key" :value="p.key" small label color="grey lighten-3" @click="applyPreset(p.key)">{{ p.label }}</v-chip>
          </v-chip-group>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="auto">
          <v-btn color="primary" depressed small :loading="loading" :disabled="!filtersChanged" @click="load">
            <v-icon left small>mdi-refresh</v-icon>Aplicar período
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>
    <v-progress-linear v-if="loading" indeterminate color="primary" height="2"></v-progress-linear>
    <v-divider v-else></v-divider>

    <v-alert v-if="error" type="error" dense text class="ma-4">{{ error }}</v-alert>

    <v-card-text v-if="data" class="pt-4">
      <!-- ───────── Ficha del suplidor ───────── -->
      <v-card outlined class="pa-4 mb-4">
        <v-row dense>
          <v-col cols="12" md="7">
            <div class="text-overline grey--text">Suplidor</div>
            <div class="text-h6 font-weight-bold">{{ data.supplier.name }}</div>
            <div class="text-body-2 grey--text text--darken-2 mt-1">
              <span v-if="data.supplier.document_id"><b>{{ data.supplier.document_type || 'Documento' }}</b> {{ data.supplier.document_id }}</span>
              <span v-if="data.supplier.credit_terms" class="ml-4"><b>Crédito</b> {{ data.supplier.credit_terms }}</span>
              <span v-if="data.supplier.currency_code" class="ml-4"><b>Moneda</b> {{ data.supplier.currency_code }}</span>
            </div>
            <div v-if="data.supplier.contacts.length" class="text-caption grey--text mt-1">
              <span v-for="(c, i) in data.supplier.contacts" :key="i" class="mr-3">
                <v-icon x-small>mdi-card-account-phone-outline</v-icon> {{ c.type ? c.type + ': ' : '' }}{{ c.value }}
              </span>
            </div>
          </v-col>
          <v-col cols="12" md="5" class="text-md-right">
            <div class="text-overline grey--text">Unidad de negocio</div>
            <div class="text-subtitle-1 font-weight-bold">{{ data.business_unit.name }}</div>
            <div class="text-body-2 grey--text text--darken-2">{{ data.location ? data.location.name : 'Todas las sucursales con acceso' }}</div>
            <div class="text-body-2 mt-1">
              <v-chip x-small label outlined class="mr-1"><v-icon x-small left>mdi-calendar-range</v-icon>{{ periodLabel }}</v-chip>
              <v-chip x-small label outlined>{{ data.counts.movements.toLocaleString() }} movimientos</v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- ───────── Resumen por moneda ───────── -->
      <v-row dense>
        <v-col v-for="m in data.summary" :key="m.currency_code" cols="12" :md="data.summary.length > 1 ? 6 : 12">
          <v-card outlined class="pa-4 fill-height">
            <div class="d-flex align-center">
              <v-chip small label color="grey darken-3" dark class="font-weight-bold">{{ m.currency_code }}</v-chip>
              <span class="ml-2 text-caption grey--text">{{ m.pending_count }} con saldo de {{ m.invoices_count }} facturas</span>
              <v-spacer></v-spacer>
              <div class="text-right">
                <div class="text-overline grey--text" style="line-height: 1">Saldo</div>
                <div class="text-h5 font-weight-bold" :class="m.balance > 0.1 ? 'error--text text--darken-1' : 'success--text'">{{ fmtMoney(m.balance) }}</div>
              </div>
            </div>
            <v-divider class="my-3"></v-divider>
            <v-row dense>
              <v-col v-for="k in kpis" :key="k.key" cols="4" sm="2">
                <div class="text-caption grey--text">{{ k.label }}</div>
                <div class="text-body-2 font-weight-medium">{{ fmtMoney(m[k.key]) }}</div>
              </v-col>
            </v-row>
            <div class="mt-3">
              <div class="d-flex justify-space-between text-caption grey--text mb-1">
                <span>Antigüedad del saldo</span>
                <span v-if="m.balance > 0">Vencido {{ fmtPct((m.balance - m.aging.current) / m.balance * 100) }}</span>
              </div>
              <div class="supplier-statement__aging">
                <div v-for="a in agingBuckets" :key="a.key" :class="a.cls" :style="{ width: agingWidth(m, a.key) }" :title="a.label + ': ' + fmtMoney(m.aging[a.key])"></div>
              </div>
              <div class="d-flex flex-wrap mt-1">
                <span v-for="a in agingBuckets" :key="a.key" class="text-caption mr-3">
                  <span class="supplier-statement__dot" :class="a.cls"></span>{{ a.label }} <b>{{ fmtMoney(m.aging[a.key]) }}</b>
                </span>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ───────── Pestañas ───────── -->
      <v-card outlined class="mt-4">
        <v-tabs v-model="tab" color="primary" show-arrows>
          <v-tab>Movimientos <v-chip x-small class="ml-2">{{ data.counts.movements }}</v-chip></v-tab>
          <v-tab>Facturas <v-chip x-small class="ml-2">{{ visibleInvoices.length }}</v-chip></v-tab>
          <v-tab>Pagos <v-chip x-small class="ml-2">{{ visiblePayments.length }}</v-chip></v-tab>
          <v-tab>Notas <v-chip x-small class="ml-2">{{ visibleNotes.length }}</v-chip></v-tab>
          <v-spacer></v-spacer>
          <v-text-field v-model="search" dense outlined hide-details clearable placeholder="Filtrar en la pestaña"
            prepend-inner-icon="mdi-magnify" class="supplier-statement__search my-1 mr-2"></v-text-field>
        </v-tabs>
        <v-divider></v-divider>
        <v-tabs-items v-model="tab">
          <!-- Movimientos -->
          <v-tab-item>
            <div class="px-3 pt-2 text-caption grey--text">
              Factura suma por su asiento contable, pago resta por el monto aplicado. El saldo corrido cuadra con el saldo del sistema.
            </div>
            <v-data-table dense :headers="movementHeaders" :items="data.movements" :search="search" :items-per-page="25"
              :footer-props="footerProps" sort-by="date" :sort-desc="[true]" class="elevation-0">
              <template v-slot:[`item.date`]="{ item }">{{ fmtDate(item.date) }}</template>
              <template v-slot:[`item.kind`]="{ item }">
                <v-chip x-small label :color="kindColor(item.kind)" :dark="item.kind !== 'opening'">{{ kindLabel(item.kind) }}</v-chip>
              </template>
              <template v-slot:[`item.reference`]="{ item }">
                <span :class="{ 'warning--text': item.unrecorded }">{{ item.reference }}</span>
                <span v-if="item.kind === 'payment' && item.invoices" class="grey--text"> → {{ item.invoices }}</span>
              </template>
              <template v-slot:[`item.debit`]="{ item }"><span v-if="item.debit">{{ fmtMoney(item.debit) }}</span></template>
              <template v-slot:[`item.credit`]="{ item }"><span v-if="item.credit" class="success--text">{{ fmtMoney(item.credit) }}</span></template>
              <template v-slot:[`item.balance`]="{ item }"><b>{{ fmtMoney(item.balance) }}</b></template>
            </v-data-table>
          </v-tab-item>

          <!-- Facturas -->
          <v-tab-item>
            <div class="d-flex align-center px-3 pt-2">
              <v-btn-toggle v-model="invoiceScope" mandatory dense color="primary">
                <v-btn small value="pending">Con saldo</v-btn>
                <v-btn small value="period">Del período</v-btn>
                <v-btn small value="all">Todas hasta el corte</v-btn>
              </v-btn-toggle>
              <v-spacer></v-spacer>
              <span class="text-caption grey--text">{{ invoiceScopeTotals }}</span>
            </div>
            <v-data-table dense :headers="invoiceHeaders" :items="visibleInvoices" :search="search" :items-per-page="25"
              :footer-props="footerProps" sort-by="emission_date" :sort-desc="[true]" class="elevation-0">
              <template v-slot:[`item.emission_date`]="{ item }">{{ fmtDate(item.emission_date) }}</template>
              <template v-slot:[`item.expire_at`]="{ item }">{{ fmtDate(item.expire_at) }}</template>
              <template v-slot:[`item.days_overdue`]="{ item }">
                <v-chip v-if="item.pending_amount > 0.1 && item.days_overdue > 0" x-small :color="item.days_overdue > 90 ? 'error' : item.days_overdue > 30 ? 'warning' : 'success'" dark>{{ item.days_overdue }}</v-chip>
              </template>
              <template v-slot:[`item.total_amount`]="{ item }">{{ fmtMoney(item.total_amount) }}</template>
              <template v-slot:[`item.total_paid`]="{ item }">{{ fmtMoney(item.total_paid) }}</template>
              <template v-slot:[`item.total_credits`]="{ item }">{{ fmtMoney(item.total_credits) }}</template>
              <template v-slot:[`item.pending_amount`]="{ item }">
                <b :class="item.pending_amount > 0.1 ? 'error--text' : ''">{{ fmtMoney(item.pending_amount) }}</b>
              </template>
              <template v-slot:[`item.state`]="{ item }">
                <v-chip x-small label :color="stateColor(item.state)" dark>{{ item.state }}</v-chip>
                <v-tooltip v-if="!item.recorded" top>
                  <template v-slot:activator="{ on }"><v-icon small color="warning" class="ml-1" v-on="on">mdi-alert-circle-outline</v-icon></template>
                  <span>Factura sin asiento contable</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- Pagos -->
          <v-tab-item>
            <div class="d-flex align-center px-3 pt-2">
              <v-btn-toggle v-model="paymentScope" mandatory dense color="primary">
                <v-btn small value="period">Del período</v-btn>
                <v-btn small value="all">Todos hasta el corte</v-btn>
              </v-btn-toggle>
              <v-spacer></v-spacer>
              <span class="text-caption grey--text">{{ paymentScopeTotals }}</span>
            </div>
            <v-data-table dense :headers="paymentHeaders" :items="visiblePayments" :search="search" :items-per-page="25"
              :footer-props="footerProps" sort-by="payment_date" :sort-desc="[true]" class="elevation-0">
              <template v-slot:[`item.payment_date`]="{ item }">{{ fmtDate(item.payment_date) }}</template>
              <template v-slot:[`item.check_number`]="{ item }">
                <div v-if="item.check_number">No. {{ String(item.check_number).trim() }}</div>
                <div class="text-caption grey--text">{{ item.account_name }}<span v-if="item.bank_name"> · {{ item.bank_name }}</span></div>
              </template>
              <template v-slot:[`item.invoices`]="{ item }">
                <span class="text-caption">{{ item.invoices.map(i => i.invoice_number).join(', ') }}</span>
              </template>
              <template v-slot:[`item.applied_amount`]="{ item }">
                <div v-for="a in item.applied_by_currency" :key="a.currency_code"><b>{{ fmtMoney(a.amount) }}</b> <small>{{ a.currency_code }}</small></div>
              </template>
              <template v-slot:[`item.status_name`]="{ item }">
                <v-chip x-small label :color="item.status_id === 4 ? 'error' : item.status_id === 3 ? 'success' : 'warning'" dark>{{ item.status_name }}</v-chip>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- Notas -->
          <v-tab-item>
            <v-data-table dense :headers="noteHeaders" :items="visibleNotes" :search="search" :items-per-page="25"
              :footer-props="footerProps" sort-by="note_date" :sort-desc="[true]" class="elevation-0"
              no-data-text="Este suplidor no tiene notas de crédito ni débito registradas en el período">
              <template v-slot:[`item.note_date`]="{ item }">{{ fmtDate(item.note_date) }}</template>
              <template v-slot:[`item.note_type`]="{ item }">
                <v-chip x-small label :color="item.note_type === 'CREDIT' ? 'success' : 'warning'" dark>{{ item.note_type === 'CREDIT' ? 'Crédito' : 'Débito' }}</v-chip>
              </template>
              <template v-slot:[`item.total_amount`]="{ item }">{{ fmtMoney(item.total_amount) }} <small>{{ item.currency_code }}</small></template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-card-text>

    <!-- ───────── PDF personalizado ───────── -->
    <v-dialog v-model="customDialog" max-width="520">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">PDF personalizado</v-card-title>
        <v-card-text>
          <div class="text-caption grey--text mb-2">Período: {{ periodLabel }}. Cambia las fechas en la barra superior si necesitas otro.</div>
          <v-checkbox v-model="sections.summary" dense hide-details label="Resumen ejecutivo, resumen por moneda y antigüedad"></v-checkbox>
          <v-checkbox v-model="sections.movements" dense hide-details label="Movimientos del período con saldo corrido"></v-checkbox>
          <v-checkbox v-model="sections.pending_invoices" dense hide-details label="Facturas con saldo a la fecha de corte"></v-checkbox>
          <v-checkbox v-model="sections.period_invoices" dense hide-details label="Todas las facturas emitidas en el período"></v-checkbox>
          <v-checkbox v-model="sections.payments" dense hide-details label="Historial de pagos del período"></v-checkbox>
          <v-checkbox v-model="sections.notes" dense hide-details label="Notas de crédito y débito del período"></v-checkbox>
          <v-select v-model="maxLines" :items="[500, 1500, 5000]" label="Máximo de líneas por tabla" dense outlined hide-details class="mt-4"
            style="max-width: 240px"></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="customDialog = false">Cancelar</v-btn>
          <v-btn color="primary" depressed :loading="exportingPdf" @click="exportPdf('custom')">Generar PDF</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import ExcelJS from "exceljs";
import moment from "moment";

export default {
  name: "SupplierStatement",
  props: {
    entityId: { type: Number, required: true },
    businessUnitId: { type: Number, required: true },
    locationId: { type: Number, default: null },
    locationAccess: { type: Array, default: () => [] },
    dateStart: { type: String, default: null },
    dateEnd: { type: String, required: true },
  },
  data() {
    return {
      loading: false,
      exportingPdf: false,
      exportingExcel: false,
      error: null,
      data: null,
      tab: 0,
      search: "",
      filters: { dateStart: this.dateStart || null, dateEnd: this.dateEnd },
      loadedFilters: null,
      preset: this.dateStart ? "custom" : "all",
      menuStart: false,
      menuEnd: false,
      invoiceScope: "pending",
      paymentScope: "period",
      customDialog: false,
      sections: { summary: true, movements: true, pending_invoices: true, period_invoices: false, payments: true, notes: false },
      maxLines: 1500,
      footerProps: { "items-per-page-options": [25, 50, 100, -1] },
      presets: [
        { key: "month", label: "Este mes" },
        { key: "3m", label: "3 meses" },
        { key: "6m", label: "6 meses" },
        { key: "year", label: "Este año" },
        { key: "all", label: "Todo" },
      ],
      kpis: [
        { key: "invoiced", label: "Facturado" },
        { key: "paid", label: "Pagado" },
        { key: "credits", label: "N. crédito" },
        { key: "debits", label: "N. débito" },
        { key: "withholdings", label: "Retenciones" },
        { key: "ledger_amount", label: "Asentado" },
      ],
      agingBuckets: [
        { key: "current", label: "Vigente", cls: "ag-current" },
        { key: "d1_30", label: "1-30", cls: "ag-30" },
        { key: "d31_60", label: "31-60", cls: "ag-60" },
        { key: "d61_90", label: "61-90", cls: "ag-90" },
        { key: "d90_plus", label: "+90", cls: "ag-90plus" },
      ],
      movementHeaders: [
        { text: "Fecha", value: "date", width: 100 },
        { text: "Tipo", value: "kind", width: 90 },
        { text: "Documento", value: "document" },
        { text: "Referencia", value: "reference" },
        { text: "Mon.", value: "currency_code", width: 60 },
        { text: "Débito", value: "debit", align: "end" },
        { text: "Crédito", value: "credit", align: "end" },
        { text: "Saldo", value: "balance", align: "end" },
      ],
      invoiceHeaders: [
        { text: "Factura", value: "invoice_number" },
        { text: "No. suplidor", value: "supplier_invoice_number" },
        { text: "Emisión", value: "emission_date" },
        { text: "Vence", value: "expire_at" },
        { text: "Días", value: "days_overdue", align: "center" },
        { text: "Sucursal", value: "location_name" },
        { text: "Mon.", value: "currency_code" },
        { text: "Total", value: "total_amount", align: "end" },
        { text: "Pagado", value: "total_paid", align: "end" },
        { text: "N. crédito", value: "total_credits", align: "end" },
        { text: "Saldo", value: "pending_amount", align: "end" },
        { text: "Estado", value: "state", align: "center" },
      ],
      paymentHeaders: [
        { text: "Fecha", value: "payment_date" },
        { text: "Pago", value: "payment_id" },
        { text: "Tipo", value: "payment_type_name" },
        { text: "Cheque / cuenta", value: "check_number" },
        { text: "Facturas", value: "invoices", sortable: false },
        { text: "Aplicado", value: "applied_amount", align: "end", sortable: false },
        { text: "Registró", value: "paid_by" },
        { text: "Estado", value: "status_name", align: "center" },
        { text: "Nota", value: "note" },
      ],
      noteHeaders: [
        { text: "Fecha", value: "note_date" },
        { text: "Tipo", value: "note_type" },
        { text: "Número", value: "note_number" },
        { text: "Factura", value: "invoice_number" },
        { text: "Monto", value: "total_amount", align: "end" },
        { text: "Motivo", value: "reason" },
      ],
    };
  },
  computed: {
    filtersChanged() {
      if (!this.loadedFilters) return true;
      return this.filters.dateStart !== this.loadedFilters.dateStart || this.filters.dateEnd !== this.loadedFilters.dateEnd;
    },
    periodLabel() {
      const f = this.loadedFilters || this.filters;
      return f.dateStart ? `${this.fmtDate(f.dateStart)} al ${this.fmtDate(f.dateEnd)}` : `Hasta el ${this.fmtDate(f.dateEnd)}`;
    },
    visibleInvoices() {
      if (!this.data) return [];
      const inv = this.data.invoices;
      if (this.invoiceScope === "pending") return inv.filter((i) => Math.abs(i.pending_amount) > 0.1);
      if (this.invoiceScope === "period") return inv.filter((i) => this.inPeriod(i.emission_date));
      return inv;
    },
    visiblePayments() {
      if (!this.data) return [];
      if (this.paymentScope === "period") return this.data.payments.filter((p) => this.inPeriod(p.payment_date));
      return this.data.payments;
    },
    visibleNotes() {
      if (!this.data) return [];
      return this.data.notes.filter((n) => this.inPeriod(n.note_date));
    },
    invoiceScopeTotals() {
      const key = this.invoiceScope === "pending" ? "pending_amount" : "total_amount";
      return `${this.invoiceScope === "pending" ? "Saldo" : "Facturado"}: ${this.totalsByCurrency(this.visibleInvoices, key)}`;
    },
    paymentScopeTotals() {
      const active = this.visiblePayments.filter((p) => p.active);
      return `Pagado: ${this.totalsByCurrency(active, "applied_amount")}`;
    },
  },
  watch: {
    entityId() { this.resetFilters(); this.load(); },
    businessUnitId() { this.resetFilters(); this.load(); },
    locationId() { this.load(); },
  },
  created() {
    this.load();
  },
  methods: {
    resetFilters() {
      this.filters = { dateStart: this.dateStart || null, dateEnd: this.dateEnd };
      this.preset = this.dateStart ? "custom" : "all";
    },
    inPeriod(d) {
      const f = this.loadedFilters || this.filters;
      return !!d && (!f.dateStart || d >= f.dateStart) && d <= f.dateEnd;
    },
    applyPreset(key) {
      const end = moment(this.filters.dateEnd || undefined);
      const starts = {
        month: end.clone().startOf("month"),
        "3m": end.clone().subtract(3, "months").add(1, "day"),
        "6m": end.clone().subtract(6, "months").add(1, "day"),
        year: end.clone().startOf("year"),
        all: null,
      };
      if (!(key in starts)) return;
      this.filters.dateStart = starts[key] ? starts[key].format("YYYY-MM-DD") : null;
      this.preset = key;
      this.load();
    },
    requestBody() {
      return {
        entity_id: this.entityId,
        business_unit_id: this.businessUnitId,
        location_id: this.locationId || null,
        location_access: this.locationAccess,
        date_start: this.filters.dateStart || null,
        date_end: this.filters.dateEnd,
      };
    },
    async load() {
      if (!this.entityId || !this.businessUnitId) return;
      this.loading = true;
      this.error = null;
      const seq = (this._seq = (this._seq || 0) + 1);
      const requested = { ...this.filters };
      try {
        const res = await this.api_Post("transactions/supplier_statement", this.requestBody());
        if (seq !== this._seq) return; // llegó una respuesta más nueva
        if (res.status !== 200) throw new Error(`El servidor respondió ${res.status}`);
        this.data = await res.json();
        this.loadedFilters = requested;
      } catch (e) {
        this.error = "No se pudo cargar el estado de cuenta: " + e.message;
      } finally {
        if (seq === this._seq) this.loading = false;
      }
    },
    async exportPdf(mode) {
      this.exportingPdf = true;
      try {
        const body = { ...this.requestBody(), generated_by: ((this.$store.state.auth.user || {}).user || {}).fullname || "" };
        if (mode === "pending") body.preset = "pending";
        if (mode === "custom") { body.sections = { ...this.sections }; body.max_lines = this.maxLines; }
        if (mode === "full") body.max_lines = this.maxLines;
        const res = await this.api_PostPdf("transactions/supplier_statement_pdf", body);
        if (res.status !== 200) throw new Error(`El servidor respondió ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        this.customDialog = false;
      } catch (e) {
        this.$toast.error("No se pudo generar el PDF: " + e.message);
      } finally {
        this.exportingPdf = false;
      }
    },
    async exportExcel() {
      if (!this.data) return;
      this.exportingExcel = true;
      try {
        const wb = new ExcelJS.Workbook();
        const money = "#,##0.00";
        const addSheet = (name, columns, rows) => {
          const ws = wb.addWorksheet(name);
          ws.columns = columns.map((c) => ({ header: c.header, key: c.key, width: c.width || 16 }));
          ws.getRow(1).font = { bold: true };
          rows.forEach((r) => ws.addRow(r));
          columns.filter((c) => c.money).forEach((c) => (ws.getColumn(c.key).numFmt = money));
          return ws;
        };
        const s = this.data;
        const resumen = addSheet("Resumen", [
          { header: "Moneda", key: "currency_code", width: 10 },
          { header: "Facturas", key: "invoices_count", width: 10 },
          { header: "Con saldo", key: "pending_count", width: 10 },
          { header: "Facturado", key: "invoiced", money: true },
          { header: "Pagado", key: "paid", money: true },
          { header: "N. crédito", key: "credits", money: true },
          { header: "N. débito", key: "debits", money: true },
          { header: "Retenciones", key: "withholdings", money: true },
          { header: "Saldo", key: "balance", money: true },
          { header: "Vigente", key: "a_current", money: true },
          { header: "1-30", key: "a_1_30", money: true },
          { header: "31-60", key: "a_31_60", money: true },
          { header: "61-90", key: "a_61_90", money: true },
          { header: "+90", key: "a_90", money: true },
        ], s.summary.map((m) => ({ ...m, a_current: m.aging.current, a_1_30: m.aging.d1_30, a_31_60: m.aging.d31_60, a_61_90: m.aging.d61_90, a_90: m.aging.d90_plus })));
        resumen.spliceRows(1, 0,
          [`Estado de cuenta de suplidor: ${s.supplier.name} (${s.supplier.document_id})`],
          [`Unidad de negocio: ${s.business_unit.name} · ${s.location ? s.location.name : "Todas las sucursales"} · ${this.periodLabel}`],
          []);
        resumen.getRow(1).font = { bold: true, size: 13 };
        addSheet("Movimientos", [
          { header: "Fecha", key: "date", width: 12 },
          { header: "Tipo", key: "kind", width: 12 },
          { header: "Documento", key: "document", width: 30 },
          { header: "Referencia", key: "reference", width: 40 },
          { header: "Facturas", key: "invoices", width: 40 },
          { header: "Moneda", key: "currency_code", width: 8 },
          { header: "Débito", key: "debit", money: true },
          { header: "Crédito", key: "credit", money: true },
          { header: "Saldo", key: "balance", money: true },
        ], s.movements.map((m) => ({ ...m, kind: this.kindLabel(m.kind) })));
        addSheet("Facturas", [
          { header: "Factura", key: "invoice_number", width: 24 },
          { header: "No. suplidor", key: "supplier_invoice_number", width: 20 },
          { header: "NCF", key: "invoice_ncf", width: 16 },
          { header: "Emisión", key: "emission_date", width: 12 },
          { header: "Vence", key: "expire_at", width: 12 },
          { header: "Días vencida", key: "days_overdue", width: 10 },
          { header: "Sucursal", key: "location_name", width: 26 },
          { header: "Moneda", key: "currency_code", width: 8 },
          { header: "Total", key: "total_amount", money: true },
          { header: "Asentado", key: "total_to_pay", money: true },
          { header: "Pagado", key: "total_paid", money: true },
          { header: "N. crédito", key: "total_credits", money: true },
          { header: "N. débito", key: "total_debits", money: true },
          { header: "Retenciones", key: "total_withholdings", money: true },
          { header: "Saldo", key: "pending_amount", money: true },
          { header: "Estado", key: "state", width: 10 },
        ], s.invoices);
        addSheet("Pagos", [
          { header: "Fecha", key: "payment_date", width: 12 },
          { header: "Pago", key: "payment_id", width: 10 },
          { header: "Tipo", key: "payment_type_name", width: 22 },
          { header: "Cheque", key: "check_number", width: 16 },
          { header: "Cuenta", key: "account_name", width: 28 },
          { header: "Banco", key: "bank_name", width: 24 },
          { header: "Facturas", key: "invoices_text", width: 50 },
          { header: "Moneda pago", key: "currency_code", width: 10 },
          { header: "Total pago", key: "payment_total", money: true },
          { header: "Aplicado", key: "applied_amount", money: true },
          { header: "Estado", key: "status_name", width: 12 },
          { header: "Registró", key: "paid_by", width: 24 },
          { header: "Nota", key: "note", width: 40 },
        ], s.payments.map((p) => ({ ...p, check_number: p.check_number ? String(p.check_number).trim() : "", invoices_text: p.invoices.map((i) => `${i.invoice_number} (${this.fmtMoney(i.amount)} ${i.currency_code})`).join("; ") })));
        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Estado de cuenta ${s.supplier.name} ${s.period.date_end}.xlsx`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } catch (e) {
        this.$toast.error("No se pudo generar el Excel: " + e.message);
      } finally {
        this.exportingExcel = false;
      }
    },
    totalsByCurrency(rows, key) {
      const acc = {};
      for (const r of rows) acc[r.currency_code] = (acc[r.currency_code] || 0) + (Number(r[key]) || 0);
      const parts = Object.entries(acc).map(([cc, v]) => `${this.fmtMoney(v)} ${cc}`);
      return parts.length ? parts.join(" · ") : "0.00";
    },
    agingWidth(m, key) {
      if (!m.balance || m.balance <= 0) return "0%";
      return `${Math.max(0, (m.aging[key] / m.balance) * 100)}%`;
    },
    fmtMoney(v) {
      return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0);
    },
    fmtPct(v) {
      return `${(Number(v) || 0).toFixed(1)}%`;
    },
    fmtDate(iso) {
      if (!iso) return "";
      const [y, m, d] = String(iso).slice(0, 10).split("-");
      return d && m && y ? `${d}/${m}/${y}` : iso;
    },
    kindLabel(kind) {
      return { opening: "Saldo inicial", invoice: "Factura", payment: "Pago" }[kind] || kind;
    },
    kindColor(kind) {
      return { opening: "grey lighten-2", invoice: "blue-grey darken-1", payment: "success" }[kind] || "grey";
    },
    stateColor(state) {
      return { pagada: "success", parcial: "warning", pendiente: "error", sobrepago: "purple" }[state] || "grey";
    },
  },
};
</script>

<style scoped>
.supplier-statement__search {
  max-width: 260px;
}
.supplier-statement__filters {
  border-bottom: 1px solid #e0e0e0;
}
.supplier-statement__aging {
  display: flex;
  height: 10px;
  border-radius: 5px;
  overflow: hidden;
  background: #eceff1;
}
.supplier-statement__aging > div {
  height: 100%;
}
.supplier-statement__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  margin-right: 4px;
  vertical-align: middle;
}
.ag-current { background: #78909c; }
.ag-30 { background: #90a4ae; }
.ag-60 { background: #ffb74d; }
.ag-90 { background: #ff8a65; }
.ag-90plus { background: #c62828; }
</style>
